"use client";

import { useRef } from "react";
import { Phone, Plus } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const faqs = [{
  q: "How does Handy Heroes match Atlanta homeowners with appliance repair pros?",
  a: "When you submit a service request, we match your appliance type and mechanical problem with independent local contractors in your Atlanta neighborhood who service that brand and issue."
}, {
  q: "Are the contractors in your network vetted?",
  a: "Yes, contractors undergo a vetting process before joining the Handy Heroes network. You can review our safety guidelines on the Trust & Safety page."
}, {
  q: "How do estimates and repair pricing work?",
  a: "Pricing is set directly by each independent technician, not by a fixed catalog. The pro inspects your appliance, identifies the required parts and labor, and provides their quote before performing work."
}, {
  q: "Can I request availability for urgent appliance repairs?",
  a: "Yes, when submitting your request or calling (470) 847-9076, let us know if your repair is urgent, such as a warming refrigerator or leaking washer, and we will check current pro availability in your area."
}, {
  q: "Which appliance brands do local pros service?",
  a: "You can connect with local pros who repair major household brands including Whirlpool, GE, Samsung, LG, Maytag, Bosch, KitchenAid, and Frigidaire appliances."
}, {
  q: "Which areas in metro Atlanta are covered?",
  a: "Handy Heroes connects homeowners across Atlanta, including Buckhead, Midtown, Decatur, Sandy Springs, Roswell, Alpharetta, Marietta, and surrounding metro communities."
}, {
  q: "What types of appliances can local pros repair?",
  a: "Pros in the network repair refrigerators, freezers, ice makers, washing machines, dryers, dishwashers, ovens, ranges, stoves, and garbage disposals."
}, {
  q: "How do I apply as an independent contractor?",
  a: "Contractors can visit our 'Join the Network' page to submit their application and credentials to receive homeowner job requests across metro Atlanta."
}];
export default function Faq() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".intro-item", {
        y: 20
      });
      gsap.to(".intro-item", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.11,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)" as unknown as string,
        scrollTrigger: {
          trigger: el,
          start: "top 82%",
          once: true
        }
      });
      gsap.set(".faq-row", {
        y: 16
      });
      gsap.to(".faq-row", {
        opacity: 1,
        y: 0,
        duration: 0.55,
        stagger: 0.06,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".faq-list",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".intro-item", ".faq-row"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="32593" id="appliancerepairfaq" data-nav="light" className="relative overflow-hidden bg-background py-28 md:py-40">
      <svg aria-hidden="true" viewBox="0 0 800 200" preserveAspectRatio="none" className="pointer-events-none absolute right-[-8%] top-1/3 hidden h-[380px] w-[70%] opacity-[0.08] lg:block">
        <path d="M0 180 L120 120 L180 160 L260 80 L340 140 L420 60 L500 130 L580 90 L660 150 L740 100 L800 140" fill="none" stroke="var(--foreground)" strokeWidth="1.5" />
      </svg>

      <div ref={container} className="relative mx-auto w-full max-w-[1440px] px-5 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="intro-item mb-8 flex items-center gap-3" style={{
              opacity: 0
            }}>
                <span aria-hidden="true" className="inline-block h-2 w-px" style={{
                backgroundColor: "var(--primary)"
              }} />
                <span className="font-default text-[13px] font-semibold uppercase leading-none" style={{
                letterSpacing: "0.22em",
                color: "var(--primary)"
              }}>
                  {"Help Center"}
                </span>
                <span aria-hidden="true" className="inline-block h-2 w-px" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>

              <h2 className="intro-item font-serif font-semibold uppercase text-foreground" style={{
              opacity: 0,
              fontSize: "clamp(2.5rem, 5.6vw, 5rem)",
              lineHeight: 0.95,
              letterSpacing: "-0.005em"
            }}>
                {"Common /"}
                <br />
                {"Queries."}
              </h2>

              <p className="intro-item mt-8 max-w-md font-default text-muted-foreground" style={{
              opacity: 0,
              fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
              lineHeight: 1.6
            }}>
                {"Common questions about finding and booking independent appliance repair pros in Atlanta. For specific questions, contact our support team."}
              </p>

              <div className="intro-item mt-10" style={{
              opacity: 0
            }}>
                <Link to="/ContactPage" className="group inline-flex items-center gap-3 font-default text-[15px] font-medium text-foreground">
                  <Phone className="h-4 w-4" style={{
                  color: "var(--primary)"
                }} aria-hidden="true" />
                  <span className="uppercase" style={{
                  letterSpacing: "0.08em",
                  borderBottom: "1px solid var(--primary)",
                  paddingBottom: "4px"
                }}>
                    {"Contact Support"}
                  </span>
                  <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1" style={{
                  color: "var(--primary)"
                }}>
                    {"→"}
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="faq-list w-full border-b border-border">
              {faqs.map((item, i) => <AccordionItem key={i} data-index={i} value={`faq-${i}`} className="faq-row border-b-0 border-t border-border" style={{
              opacity: 0
            }}>
                  <AccordionTrigger className="group flex w-full items-start justify-between gap-6 py-8 pr-2 text-left font-default text-[18px] font-medium leading-snug text-foreground hover:no-underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:text-[20px] [&>svg]:hidden">
                    <span className="flex-1">{item.q}</span>
                    <span aria-hidden="true" className="relative mt-1 flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-300 group-data-[state=open]:rotate-45" style={{
                  color: "var(--primary)"
                }}>
                      <Plus className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-2 pt-3 md:pr-10">
                    <p className="font-default text-muted-foreground" style={{
                  fontSize: "17px",
                  lineHeight: 1.65
                }}>
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}